import { useRef, useState, useEffect } from "react";
import { apiFetch } from "../utility/apiFetch.js";
import { useParams, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import Gauge from "../components/Gauge.jsx";
import InlineEdit from "../components/InlineEdit.jsx";
import MetricChart from "../components/MetricChart.jsx";
import TimeRangePicker from "../components/TimeRangePicker.jsx";
import { PHASE_COLORS } from "../components/GrowPhaseBar.jsx";
import usePlan from "../hooks/usePlan.js";
import { useIsStale } from "../hooks/useIsStale.js";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeftIcon, PencilIcon } from "@heroicons/react/20/solid";
import { computeVPD } from "../utility/vpd.js";

const ONE_HOUR_MS = 60 * 60 * 1000;

export default function DeviceDetail() {
  const { deviceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [timeRange, setTimeRange] = useState(() => ({ start: Date.now() - ONE_HOUR_MS, end: Date.now() }));
  const sseStartedRef = useRef(false);
  const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
  const { isPro } = usePlan();

  const sensor_order = ["orange1", "blue1", "white1"];
  const metric_order = ["temperature", "humidity", "co2"];

  const processDevice = (dashboardResponse, devId) => {
    const deviceData = dashboardResponse.devices?.[devId];
    if (!deviceData) return null;
    const processed = {
      ...deviceData,
      sensors: deviceData.sensors?.map(sensor => ({
        ...sensor,
        metrics: sensor.metrics.map(metric => {
          let baseUpdates = {};
          if (metric.metric === "humidity") baseUpdates = { min_value: 0, max_value: 100, title: "Humidity %RH" };
          else if (metric.metric === "co2") baseUpdates = { min_value: 0, max_value: 12000, title: "CO2 PPM" };
          else baseUpdates = { min_value: 40, max_value: 100, title: "Temperature °F" };
          const t = deviceData.thresholds?.thresholds?.[metric.metric];
          return { ...metric, ...baseUpdates, limit_low: t?.low_value, limit_high: t?.high_value };
        }),
      })) || [],
    };
    processed.sensors.sort((a, b) => sensor_order.indexOf(a.sensor_code) - sensor_order.indexOf(b.sensor_code));
    processed.sensors.forEach(s => s.metrics.sort((a, b) => metric_order.indexOf(a.metric) - metric_order.indexOf(b.metric)));
    return processed;
  };

  const getDashboard = async () => {
    try {
      const res = await fetch(apiBaseURL + "dashboard", { credentials: "include" });
      if (res.status !== 200) { setError(await res.text()); return; }
      const data = await res.json();
      const d = processDevice(data, parseInt(deviceId));
      if (!d) { setError("Device not found"); return; }
      setDevice(d);
      setLoading(false);
    } catch (e) {
      Sentry.captureException(e);
      setError(e.message);
    }
  };

  useEffect(() => { getDashboard(); }, []);

  useEffect(() => {
    if (!device || sseStartedRef.current) return;
    sseStartedRef.current = true;
    const es = new EventSource(apiBaseURL + "dashboardlive", { withCredentials: true });
    es.addEventListener("metrics", (e) => {
      const updates = JSON.parse(e.data);
      const update = updates.find(u => u.device_id === parseInt(deviceId));
      if (!update) return;
      setDevice(prev => {
        const next = structuredClone(prev);
        next.device.last_seen = update.last_seen;
        next.device.uptime = update.uptime;
        update.sensors.forEach(su => {
          const s = next.sensors.find(s => s.id === su.id);
          if (!s) return;
          s.last_seen = su.last_seen;
          su.metrics.forEach(mu => {
            const m = s.metrics.find(m => m.metric === mu.metric);
            if (m) { m.latest_value = mu.latest_value; m.last_seen = mu.last_seen; }
          });
        });
        return next;
      });
    });
    return () => es.close();
  }, [device]);

  const saveName = async (type, id, name) => {
    const path = type === "device" ? `devices/${id}/name` : `sensors/${id}/name`;
    await apiFetch(path, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
    if (type === "device") setDevice(prev => ({ ...prev, device: { ...prev.device, display_name: name || null } }));
    else setDevice(prev => ({ ...prev, sensors: prev.sensors.map(s => s.id === id ? { ...s, display_name: name || null } : s) }));
  };

  const stale = useIsStale(device?.device?.last_seen);

  if (loading) return <LayoutWide title="Device"><Spinner /></LayoutWide>;
  if (error) return <LayoutWide title="Device"><div className="p-4 text-red-600 dark:text-red-400">{error}</div></LayoutWide>;

  const dev = device.device;
  const deviceLabel = dev.display_name || dev.hostname;

  const sensorLabels = Object.fromEntries(
    (device.sensors ?? []).map(s => [s.sensor_code, s.display_name || s.sensor_name || s.sensor_code])
  );

  const activePhase = device.thresholds?.active_phase;
  const activePhaseDisplay = activePhase
    ? activePhase.charAt(0).toUpperCase() + activePhase.slice(1)
    : null;
  const phaseBadgeColor = activePhaseDisplay
    ? `${PHASE_COLORS[activePhaseDisplay] ?? "bg-gray-200"} text-white`
    : null;

  return (
    <LayoutWide title={deviceLabel}>
      <div className="container w-full rounded-xl bg-white dark:bg-gray-800 shadow p-4 min-h-40">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-sky-500 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300">
              <ArrowLeftIcon className="size-5" />
            </Link>
            <div>
              <h2 className="text-sky-800 dark:text-sky-300 text-2xl font-semibold flex items-center gap-2">
                <InlineEdit
                  value={dev.display_name}
                  placeholder={dev.hostname}
                  onSave={name => saveName("device", dev.id, name)}
                />
                {isPro && activePhaseDisplay && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${phaseBadgeColor}`}>
                    {activePhaseDisplay}
                    <Link to="/settings" className="opacity-60 hover:opacity-100">
                      <PencilIcon className="size-3" />
                    </Link>
                  </span>
                )}
              </h2>
              <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{dev.connectcode}</div>
            </div>
          </div>
          <div className={`text-sm ${stale ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
            Updated {dev.last_seen ? formatDistanceToNow(new Date(dev.last_seen), { addSuffix: true }) : "never"}
          </div>
        </div>

        {device.sensors.map(sensor => {
          const isOrange = sensor.sensor_code === "orange1";
          return (
            <div key={sensor.id} className="mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-sky-800 dark:text-sky-300 pb-2 border-b border-gray-100 dark:border-gray-700">
                <span className={`inline-block w-3 h-3 rounded-full ${isOrange ? "bg-orange-500" : "bg-sky-500"}`} />
                <InlineEdit
                  value={sensor.display_name}
                  placeholder={sensor.sensor_name}
                  onSave={name => saveName("sensor", sensor.id, name)}
                />
                <span className={`text-xs font-mono font-normal ${isOrange ? "text-orange-400 dark:text-orange-300" : "text-sky-400 dark:text-sky-300"}`}>
                  {sensor.sensor_code}
                </span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-2">
                {sensor.metrics.map(metric => (
                  <div key={metric.id}>
                    {metric.latest_value == null ? (
                      <div className="text-center text-sm text-gray-400 dark:text-gray-500 py-8">
                        {metric.title}<br />n/a
                      </div>
                    ) : (
                      <div className={stale ? "opacity-40" : undefined}>
                        <Gauge
                          title={metric.title}
                          value={metric.latest_value}
                          limitLow={metric.limit_low}
                          limitHigh={metric.limit_high}
                          minValue={metric.min_value}
                          maxValue={metric.max_value}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {(() => {
                  const tempM = sensor.metrics.find(m => m.metric === "temperature");
                  const humM = sensor.metrics.find(m => m.metric === "humidity");
                  if (!tempM || !humM) return null;
                  const vpd = (tempM.latest_value != null && humM.latest_value != null)
                    ? computeVPD(tempM.latest_value, humM.latest_value)
                    : null;
                  return (
                    <div>
                      {vpd == null ? (
                        <div className="text-center text-sm text-gray-400 dark:text-gray-500 py-8">
                          VPD kPa<br />n/a
                        </div>
                      ) : (
                        <div className={stale ? "opacity-40" : undefined}>
                          <Gauge
                            title="VPD kPa"
                            value={vpd}
                            limitLow={0.1}
                            limitHigh={0.9}
                            minValue={0}
                            maxValue={2}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}

        <div className="mt-6 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-sky-800 dark:text-sky-300">History</h3>
            <TimeRangePicker
              start={timeRange.start}
              end={timeRange.end}
              onRangeChange={setTimeRange}
            />
          </div>
          <div className="space-y-6">
            <MetricChart
              title="Temperature °F"
              metric="temperature"
              hostname={dev.hostname}
              unit="°F"
              timeRange={timeRange}
              sensorLabels={sensorLabels}
            />
            <MetricChart
              title="Humidity %RH"
              metric="humidity"
              hostname={dev.hostname}
              unit="%"
              timeRange={timeRange}
              sensorLabels={sensorLabels}
            />
            <MetricChart
              title="CO2 PPM"
              metric="co2"
              hostname={dev.hostname}
              unit="ppm"
              timeRange={timeRange}
              sensorLabels={sensorLabels}
            />
          </div>
        </div>
      </div>
    </LayoutWide>
  );
}
